import React, { useState, useEffect, useRef } from 'react'
import './UsersCharts.css'
//components
import Line from '../../../components/Charts/Line/Line'
import Donut from '../../../components/Charts/Donut/Donut'
import DashboardFilter from '../fragments/DashboardFilter'
//services
import { dashboardService } from '../../../services/Dashboard';
//utils
import { formatShortDate, getMonth } from '../../../utils/utils';
import HeaderSection from '../../../components/PdfDownload/Header'
import UsersDataTables from '../../../components/PdfDownload/Table'

const UsersCharts = () => {
  const [newUsers, setNewUsers] = useState([]);
  const [distributionUsers, setDistributionUsers] = useState([]);
  const customColors = ['#15B79E', '#0E5F79', '#F2994A'];
  const [xLabel, setXLabel] = useState('Months');
  const [selectedRange, setSelectedRange] = useState(null);
  const [reportRange, setReportRange] = useState({ start: null, end: null });
  const chartRef = useRef(null);
  const [showHiddenContent, setShowHiddenContent] = useState(false);
  useEffect(() => {
    if (showHiddenContent) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showHiddenContent]);

  useEffect(() => {
    fetchNewUsers();
    fetchDistributionUsers();
  }, []);

  const normalizeYears = (years) => {
    if (!Array.isArray(years) || years.length === 0) return [];
    return years.map((y) => (typeof y === 'object' ? Number(y.value) : Number(y))).filter(Boolean);
  };


const fetchNewUsers = async (filters = {}) => {
  try {
    let start_date, end_date;

    const years = normalizeYears(filters.years);

    if (filters.rangeDate?.start_date && filters.rangeDate?.end_date) {
      start_date = formatShortDate(filters.rangeDate.start_date, '-');
      end_date = formatShortDate(filters.rangeDate.end_date, '-');
    } else if (years.length > 0) {
      // Si se seleccionan varios años, tomar el rango completo
      const minY = Math.min(...years);
      const maxY = Math.max(...years);
      start_date = `${minY}-01-01`;
      end_date = `${maxY}-12-31`;
    } else {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 1);
      start_date = formatShortDate(date, '-');
      end_date = formatShortDate(new Date(), '-');
    }

    const group_by = filters.period ? filters.period.value : null;
    setReportRange({ start: start_date, end: end_date });
    setXLabel(group_by ? group_by : 'Months');

    const params = {
      ...(start_date && { start_date }),
      ...(end_date && { end_date }),
      ...(group_by && { group_by })
    };

    const response = await dashboardService.newUsers(params);

    if(response?.series?.[0]?.data){
      const data = response.series[0].data.map(item => {
        const year = item.x.split("-")[0]; 
        let xValue = '';

        if (group_by === 'week') xValue = item.x;
        else if (group_by === 'quarter') xValue = item.x.includes("Q") ? item.x : `Q${Math.ceil(parseInt(item.x.split("-")[1], 10) / 3)}`;
        else if (group_by === 'semester') xValue = item.x.includes("S") ? item.x : '';
        else if (group_by === 'year') xValue = item.x;
        else xValue = getMonth(item.x, group_by);

        return { x: item.x, y: item.y, year, period: xValue };
      });
      setNewUsers(data);
    }

  } catch (error) {
    console.error('Error fetching new users:', error);
  }
};

const fetchDistributionUsers = async (filters = {}) => {
  try {
    let start_date, end_date;

    const years = normalizeYears(filters.years);

    if (filters.rangeDate?.start_date && filters.rangeDate?.end_date) {
      start_date = formatShortDate(filters.rangeDate.start_date, '-');
      end_date = formatShortDate(filters.rangeDate.end_date, '-');
    } else if (years.length > 0) {
      const minY = Math.min(...years);
      const maxY = Math.max(...years);
      start_date = `${minY}-01-01`;
      end_date = `${maxY}-12-31`;
    } else {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 1);
      start_date = formatShortDate(date, '-');
      end_date = formatShortDate(new Date(), '-');
    }

    const group_by = filters.period ? filters.period.value : null;
    const user_type = filters.user_type ? filters.user_type.value : null;
    let status = filters.status ? filters.status : null;
    if (status) status = status === 'Active' ? 'active' : 'disabled';

    const params = {
      ...(start_date && { start_date }),
      ...(end_date && { end_date }),
      ...(group_by && { group_by }),
      ...(user_type && { user_type }),
      ...(status && { status }),
    };

    setReportRange({ start: start_date, end: end_date });

    const response = await dashboardService.distributionUsers(params);

    if (response?.series) {
      const mergedData = response.series.flatMap(series =>
        series.data.map(item => {
          let xValue = '';
          if (group_by === 'week') xValue = item.x;
          else if (group_by === 'quarter') xValue = item.x.includes("Q") ? item.x : `Q${Math.ceil(parseInt(item.x.split("-")[1], 10) / 3)}`;
          else if (group_by === 'semester') xValue = item.x.includes("S") ? item.x : '';
          else if (group_by === 'year') xValue = item.x;
          else xValue = getMonth(item.x, group_by);
          return { x: item.x, y: item.y, label: series.label, period: xValue };
        })
      );
      setDistributionUsers(mergedData);
    }

  } catch (error) {
    console.error('Error fetching distribution users:', error);
  }
};


  // Función para generar un color aleatorio sin repetir
  const getRandomColor = (existingColors = []) => {
    const letters = '0123456789ABCDEF';
    let color;
    let tries = 0;

    do {
      color = '#';
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      tries++;
    } while (existingColors.includes(color) && tries < 50);

    return color;
  };

  // Genera el arreglo de colores para el Donut
  const generateColors = (itemsCount) => {
    const colors = [...customColors]; // primeros 3 fijos
    const usedColors = [...customColors];

    for (let i = customColors.length; i < itemsCount; i++) {
      const newColor = getRandomColor(usedColors);
      colors.push(newColor);
      usedColors.push(newColor);
    }

    return colors;
  };


  return (
    <>
      <div className="users-charts-container">
        <DashboardFilter
          onApplyFilters={(filters) => {
            if (filters.rangeDate) {
              setSelectedRange(filters.rangeDate);
            } else {
              setSelectedRange(null);
            }
            fetchNewUsers(filters);
            fetchDistributionUsers(filters);
          }}
          chartRef={chartRef}
          dynamicHeight={true}
          includeYearFilter={true}
          onDownloadStart={() => setShowHiddenContent(true)}
          onDownloadEnd={() => setShowHiddenContent(false)}
          timeout={1500}
        />

        {/* Gráficas visibles */}
        <div className="chart-wrapper">
          <Line
            data={newUsers}
            xKey="month"
            yKey="users"
            xLabel={xLabel}
            yLabel="Users active"
            title="New Users"
            height={335}
            width={400}
          />
          <Donut
            data={distributionUsers}
            title="Distribution users"
            height={170}
            width={400}
            innerRadius={40}
            outerRadius={80}
            colors={generateColors(distributionUsers.length)}
            showLabelWithX={true}
          />
        </div>
      </div>

      {/* Bloque oculto que solo se usa para PDF */}
      {showHiddenContent && (
        <div ref={chartRef} style={{marginTop: '60px'}}>
          <HeaderSection
            title="Users"
            date={formatShortDate(new Date(), '-')}
            period={
              reportRange.start && reportRange.end
                ? `${reportRange.start} - ${reportRange.end}`
                : 'Sin fecha'
            }
          />
          <div className="chart-wrapper">
            <Line
              data={newUsers}
              xKey="month"
              yKey="users"
              xLabel={xLabel}
              yLabel="Users active"
              title="New Users"
              height={335}
              width={400}
            />
            <Donut
              data={distributionUsers}
              title="Distribution users"
              height={170}
              width={400}
              innerRadius={40}
              outerRadius={80}
              colors={generateColors(distributionUsers.length)}
              showLabelWithX={true}
            />
          </div>
          <UsersDataTables
            data1={newUsers}
            table1="Users"
            table2="Distribution Users"
            data2={distributionUsers}
            groupBy={xLabel.toLowerCase()}
          />
        </div>
      )}
    </>
  );
};
export default UsersCharts
